import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SecurityIcon from '@mui/icons-material/Security';
import { webauthnService } from '../services/webauthn';
import { useAuth } from '../context/AuthContext';

interface WebAuthnLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const WebAuthnLogin: React.FC<WebAuthnLoginProps> = ({ onSuccess, onError }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  
  const { loginWithWebAuthn } = useAuth();

  useEffect(() => {
    checkAvailability();
  }, []);

  const checkAvailability = async () => {
    setChecking(true);
    try {
      const supported = webauthnService.isSupported();
      setIsSupported(supported);
      
      if (supported) {
        const available = await webauthnService.isBiometricAvailable();
        setIsAvailable(available);
      }
    } catch (error) {
      console.error('Error checking WebAuthn availability:', error);
      setIsSupported(false);
      setIsAvailable(false);
    } finally {
      setChecking(false);
    }
  };

  const handleBiometricLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const { token, user } = await webauthnService.authenticate();
      
      // Use the existing auth context to handle the login
      if (loginWithWebAuthn) {
        await loginWithWebAuthn(token, user);
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Biometric authentication failed';
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything while checking availability
  if (checking) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          Checking biometric availability...
        </Typography>
      </Box>
    );
  }

  // Don't render if WebAuthn is not supported or available
  if (!isSupported || !isAvailable) {
    return null;
  }

  return (
    <Box>
      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="textSecondary">
          or
        </Typography>
      </Divider>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Tooltip 
        title="Use your device's biometric authentication (fingerprint, face recognition, etc.)"
        placement="top"
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={handleBiometricLogin}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : (
              <FingerprintIcon />
            )
          }
          sx={{
            py: 1.5,
            fontSize: '1rem',
            borderColor: 'secondary.main',
            color: 'secondary.main',
            '&:hover': {
              borderColor: 'secondary.dark',
              backgroundColor: 'rgba(156, 39, 176, 0.04)',
            },
            '&:disabled': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {loading ? 'Authenticating...' : 'Sign in with biometrics'}
        </Button>
      </Tooltip>

      <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
        <SecurityIcon sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
        <Typography variant="caption" color="textSecondary" align="center">
          Secure biometric authentication powered by your device
        </Typography>
      </Box>
    </Box>
  );
};

export default WebAuthnLogin;